import { useEffect, useMemo, useState } from 'react';
import { IoExit } from 'react-icons/io5';
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from 'react-icons/md';
import { NavLink } from 'react-router-dom';

import useSWR from 'swr';

import {
  BRAND_PROPERTIES,
  DASHBOARD_SUMMARY_DAYS_DIFFERENCE,
  LocalStorageKeys,
  ONE_MINUTE_MILLISECONDS,
  routes,
} from 'commons';
import {
  getAllUserTokens,
  getFinalDate,
  getInitialDate,
  removeLocalStorageItem,
  setLocalStorageItem,
  swrKeys,
} from 'helpers';
import { useAuth } from 'hooks';
import { dashboardService } from 'services';
import { SystemLogoIcon } from 'static/icons';
import { Brand, DashboardCardType, MenuNavItem } from 'types';

import {
  ChangeStoreHover,
  ConfirmExitModal,
  NavItemButton,
  NavItemHover,
} from './components';
import {
  EXCHANGE_TITLE,
  MENU_ITEMS,
  ORDER_ATTENDANCE_TITLE,
  ORDER_TITLE,
} from './constants';
import styles from './styles.module.css';

const VALID_STORES_TO_PICKING_MANAGEMENT = [
  '301753',
  '300191',
  '321595',
  '307355',
  '20340',
  '52777',
  '319444',
  '53397',
];

const PICKING_MANAGEMENT = routes.pickingManagement;

const FIVE_MINUTES_IN_MILLISECONDS = 5 * ONE_MINUTE_MILLISECONDS;

interface Props {
  closeMenu: () => void;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

export function Menu({ closeMenu, expanded, setExpanded }: Props) {
  const {
    stores: storesByBrandList,
    user,
    selectedStore,
    setSelectedStore,
  } = useAuth();
  const { isAdmin } = user;

  const filteredMenuItems = useMemo(() => {
    return MENU_ITEMS.filter(item => {
      if (isAdmin) {
        return item.isAdmin;
      }
      return item.isStore;
    });
  }, [isAdmin]);

  const [menuItems, setMenuItems] = useState<MenuNavItem[]>(filteredMenuItems);
  const [selectedItem, setSelectedItem] = useState<null | string>(null);
  const [confirmExit, setConfirmExit] = useState(false);

  const { brand, name: storeName } = selectedStore;
  const brandDisplay = BRAND_PROPERTIES[brand].shortName;
  const storeDisplay = storeName || '';
  const store = storeName || undefined;

  const [changeStore, setChangeStore] = useState(false);

  const startDate = getInitialDate({
    timeDifference: DASHBOARD_SUMMARY_DAYS_DIFFERENCE,
  });
  const endDate = getFinalDate();

  const { data } = useSWR(
    swrKeys.dashboardSummary({ brand, store }),
    () =>
      dashboardService.getDashboardSummary({
        store,
        startDate,
        endDate,
      }),
    { refreshInterval: FIVE_MINUTES_IN_MILLISECONDS, revalidateOnFocus: false }
  );

  useEffect(() => {
    setMenuItems(filteredMenuItems);
  }, [filteredMenuItems]);

  useEffect(() => {
    if (!data) return;

    setMenuItems(prevMenuItems => {
      return prevMenuItems.map(menuItem => {
        if (menuItem.title === ORDER_TITLE) {
          const updatedItemList = menuItem.itemList.map(item => {
            if (item.name === ORDER_ATTENDANCE_TITLE) {
              const waitingAttendance = data?.entries.find(
                ({ type }) => type === DashboardCardType.WaitingAttendance
              );

              return {
                ...item,
                numericalIndicator: waitingAttendance?.quantity,
              };
            }
            return item;
          });
          return { ...menuItem, itemList: updatedItemList };
        }

        if (menuItem.title === EXCHANGE_TITLE) {
          const updatedItemList = menuItem.itemList.map(item => {
            if (item.name === routes.exchangeAndReturn.name) {
              const returnRequestInProgress = data?.entries.find(
                ({ type }) => type === DashboardCardType.ReturnRequestInProgress
              );
              return {
                ...item,
                numericalIndicator: returnRequestInProgress?.quantity,
              };
            }
            return item;
          });
          return { ...menuItem, itemList: updatedItemList };
        }

        return menuItem;
      });
    });
  }, [data]);

  useEffect(() => {
    const menuOrders = MENU_ITEMS.find(({ title }) => title === ORDER_TITLE);

    if (!menuOrders) return;

    const hasPickingManagement = menuOrders.itemList.some(
      item => item.name === PICKING_MANAGEMENT.name
    );

    const showPickingManagement =
      storeName && VALID_STORES_TO_PICKING_MANAGEMENT.includes(storeName);

    if (showPickingManagement) {
      if (hasPickingManagement) return;

      if (!hasPickingManagement) {
        menuOrders.itemList.push(PICKING_MANAGEMENT);
      }
    } else {
      if (hasPickingManagement) {
        menuOrders.itemList = menuOrders.itemList.filter(
          ({ name }) => name !== PICKING_MANAGEMENT.name
        );
      }
    }
  }, [storeName]);

  const storeItems = useMemo(() => {
    function selectStore({
      store,
      brand,
      access,
    }: {
      store: string | null;
      brand: Brand;
      access: string;
    }) {
      const { stores } = getAllUserTokens();
      const currentStore = stores.find(
        storeToFind => storeToFind.brand === brand
      );
      const newSelectedStore = {
        name: store,
        brand,
        access: currentStore ? currentStore.access : access,
      };

      removeLocalStorageItem(LocalStorageKeys.Attendances);

      setLocalStorageItem(LocalStorageKeys.SelectedStore, newSelectedStore);

      setSelectedStore(newSelectedStore);
    }
    return storesByBrandList.map(storesByBrand => {
      const { brand, stores, access } = storesByBrand;

      const isBrandSelected = isAdmin && selectedStore.brand === brand;

      const storeList = stores
        .filter(store => Boolean(store))
        .map(store => {
          const isStoreSelected = selectedStore.name === store;

          return {
            title: store,
            action: () => selectStore({ store, brand, access }),
            isSelected: isStoreSelected,
          };
        });

      return {
        title: BRAND_PROPERTIES[brand]?.displayName,
        action: isAdmin
          ? () => selectStore({ store: null, brand, access })
          : undefined,
        isSelected: isBrandSelected,
        subItemList: storeList,
      };
    });
  }, [
    isAdmin,
    selectedStore.brand,
    selectedStore.name,
    setSelectedStore,
    storesByBrandList,
  ]);

  function renderChangeStoreButton() {
    const hasMultipleBrands = storesByBrandList.length > 1;
    const hasMultipleStores = storesByBrandList[0]?.stores.length > 1;
    const showStoreSelection = hasMultipleBrands || hasMultipleStores;

    function enableStoreChange() {
      if (!showStoreSelection) return;

      setChangeStore(cur => !cur);
    }

    function renderChangeButton() {
      if (changeStore) return <span>Voltar</span>;

      if (isAdmin) {
        return (
          <>
            <span
              onClick={enableStoreChange}
              className="font-bold text-sm hover:underline cursor-pointer"
            >
              {brandDisplay}
            </span>
            {hasMultipleBrands && (
              <span className="text-sm">Alterar marca</span>
            )}
          </>
        );
      }

      return (
        <>
          <span className="font-bold text-sm">
            {brandDisplay} - {storeDisplay}
          </span>
          {showStoreSelection && <span className="text-sm">Alterar loja</span>}
        </>
      );
    }

    const backgroundClassName = changeStore
      ? 'bg-transparent'
      : 'bg-neutral-500';
    const buttonClasses = `flex-col items-center justify-center mx-auto w-44 h-12
      rounded-full border-solid border border-transparent text-neutral-100
      ${showStoreSelection && 'md:hover:border-neutral-400 cursor-pointer'}`;

    return (
      <>
        <button
          onClick={enableStoreChange}
          className={`md:hidden flex ${buttonClasses} ${backgroundClassName}`}
        >
          {renderChangeButton()}
        </button>

        {expanded ? (
          <button
            onClick={enableStoreChange}
            className={`hidden md:flex ${buttonClasses} ${backgroundClassName}`}
          >
            {renderChangeButton()}
          </button>
        ) : (
          <ChangeStoreHover
            showStoreSelection={showStoreSelection}
            brandDisplay={brandDisplay}
            storeDisplay={storeDisplay}
            storeItems={storeItems}
            isAdmin={isAdmin}
            closeMenu={() => {
              setExpanded(false);
              setChangeStore(false);
            }}
          />
        )}
      </>
    );
  }

  function renderExpandAndContractButton() {
    return (
      <>
        <button
          onClick={() => closeMenu()}
          className="md:hidden flex items-center justify-center h-16 hover:bg-neutral-500 bg-inherit
            text-inherit gap-2 text-sm cursor-pointer"
        >
          <MdKeyboardDoubleArrowLeft size={32} />
          <span>Recolher menu</span>
        </button>

        <button
          onClick={() => {
            setChangeStore(false);
            setExpanded(!expanded);
          }}
          className="hidden md:flex items-center justify-center h-16 hover:bg-neutral-500 bg-inherit
            text-inherit gap-2 text-sm cursor-pointer"
        >
          {expanded ? (
            <>
              <MdKeyboardDoubleArrowLeft size={32} />
              <span>Recolher menu</span>
            </>
          ) : (
            <MdKeyboardDoubleArrowRight size={32} />
          )}
        </button>
      </>
    );
  }

  function renderExitButton() {
    const exitButtonWidthClassName = expanded ? 'md:w-44' : 'md:w-12';
    const exitSpanClassName = expanded ? '' : 'md:hidden';

    return (
      <div className="flex items-center justify-center h-16">
        <button
          onClick={() => setConfirmExit(true)}
          className={`flex items-center justify-center border-solid border border-transparent
            bg-inherit text-inherit gap-2 text-sm cursor-pointer w-44 h-12 rounded-full
            hover:border-neutral-400 ${exitButtonWidthClassName}`}
        >
          <IoExit size={32} />
          <span className={exitSpanClassName}>
            {isAdmin ? 'Sair' : 'Sair da loja'}
          </span>
        </button>
      </div>
    );
  }

  const renderNavButtonList = menuItems.map(itemProps => (
    <NavItemButton
      key={itemProps.title}
      showItems={selectedItem === itemProps.title}
      setSelectedItem={setSelectedItem}
      itemProps={itemProps}
    />
  ));

  const renderNavHoverList = menuItems.map(itemProps => (
    <NavItemHover key={itemProps.title} itemProps={itemProps} />
  ));

  const widthClassName = expanded ? 'md:w-[340px]' : 'md:w-16';

  return (
    <>
      {confirmExit && (
        <ConfirmExitModal handleClose={() => setConfirmExit(false)} />
      )}

      <div
        className={`flex flex-col justify-between bg-neutral-700 h-full text-neutral-100
          transition-all duration-300 w-dvw ${widthClassName}`}
      >
        <NavLink
          to="/"
          className="flex items-start justify-center px-1.5 cursor-pointer py-6 no-underline
            text-neutral-100"
        >
          <div
            className="w-full flex items-center justify-center min-h-10 h-10 max-h-10
              [&_svg]:max-h-full [&_svg]:w-full"
          >
            <SystemLogoIcon />
          </div>
        </NavLink>

        <div
          className={`flex flex-col flex-1 gap-6 overflow-x-hidden overflow-y-auto
            ${styles.scrollbarHide}`}
        >
          {changeStore ? (
            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="flex flex-col h-full">
                <div className="flex justify-center py-4 px-2 border-b border-neutral-500/30">
                  <span className="text-base font-bold text-neutral-100">
                    {isAdmin ? 'Selecione a marca' : 'Selecione a loja'}
                  </span>
                </div>

                <div
                  className={`flex flex-col flex-1 overflow-y-auto mt-2 min-h-0 ${styles.menuScrollbar}`}
                >
                  {storeItems.map(
                    ({ title, action, isSelected, subItemList }, index) => (
                      <div
                        className="flex flex-col mb-1 flex-shrink-0"
                        key={title}
                      >
                        {isAdmin ? (
                          <button
                            onClick={() => {
                              if (action) {
                                action();
                                setChangeStore(cur => !cur);
                              }
                            }}
                            className={`text-start cursor-pointer p-4 text-sm font-semibold transition-all duration-200
                              border border-transparent ${
                              isSelected
                                  ? 'bg-primary-400/80 text-white border-primary-300 shadow-sm'
                                  : 'text-neutral-100 hover:bg-neutral-600/70 hover:border-neutral-500/50'
                              }`}
                          >
                            {title}
                          </button>
                        ) : (
                          <div>
                            <div className="py-2 px-3 mb-1">
                              <span className="text-sm font-semibold text-neutral-200 uppercase tracking-wide">
                                {title}
                              </span>
                            </div>

                            <div className="flex flex-col gap-1">
                              {subItemList.map(
                                ({ title, action, isSelected }) => (
                                  <button
                                    key={title}
                                    onClick={() => {
                                      action();
                                    }}
                                    className={`text-start cursor-pointer p-4 text-sm transition-all duration-200 border
                                      border-transparent ${
                                      isSelected
                                          ? 'bg-primary-400/80 text-white border-primary-300 shadow-sm font-medium'
                                          : 'text-neutral-100 hover:bg-neutral-600/70 hover:border-neutral-500/50'
                                      }`}
                                  >
                                    {title}
                                  </button>
                                )
                              )}
                            </div>

                            {index < storeItems.length - 1 && (
                              <div className="h-px bg-neutral-600/50 mx-2 my-3" />
                            )}
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="md:hidden flex flex-col">
                {renderNavButtonList}
              </div>

              <div className="hidden md:flex flex-col">
                {expanded ? renderNavButtonList : renderNavHoverList}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col">
          <div className="flex flex-col gap-2 pb-2">
            {renderChangeStoreButton()}
            {renderExitButton()}
          </div>

          <span className="mx-1.5 flex h-px bg-neutral-400" />

          {renderExpandAndContractButton()}
        </div>
      </div>
    </>
  );
}
